/**
 * Settings Registry
 * 
 * Frontend registry for plugin-provided settings.
 * Part of Core Hardening Phase 2.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  getSettings,
  getSettingsSection,
  getSettingsValues,
  saveSettingsValues,
  resetSettings,
  type SettingsSection,
  type SettingsPage,
  type SettingsGroup,
  type SettingsField,
  type SettingsFieldOption,
  type SettingsFieldType,
  type SettingsValidationResult,
} from '../../api/platform';

// Re-export types for consumers
export type {
  SettingsSection,
  SettingsPage,
  SettingsGroup,
  SettingsField,
  SettingsFieldOption,
  SettingsFieldType,
  SettingsValidationResult,
};

/**
 * Settings registry state
 */
interface SettingsRegistryState {
  // Data
  sections: SettingsSection[];
  
  // Values cache: Map<`${pluginId}:${pageId}`, Record<string, any>>
  valuesCache: Map<string, Record<string, any>>;
  
  // State
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // Actions
  load: () => Promise<void>;
  refresh: () => Promise<void>;
  getSection: (sectionId: string) => SettingsSection | undefined;
  getPage: (sectionId: string, pageId: string) => SettingsPage | undefined;
  getValues: (pluginId: string, pageId: string) => Promise<Record<string, any>>;
  saveValues: (pluginId: string, pageId: string, values: Record<string, any>) => Promise<SettingsValidationResult>;
  resetToDefaults: (pluginId: string, pageId: string) => Promise<void>;
  getVisibleSections: (userPermissions: string[]) => SettingsSection[];
  reset: () => void;
}

/**
 * Check if user has required permissions
 */
function hasPermissions(requiredPermissions: string[] | undefined, userPermissions: string[]): boolean {
  if (!requiredPermissions?.length) return true;
  if (userPermissions.includes('admin')) return true;
  return requiredPermissions.every(p => userPermissions.includes(p));
}

/**
 * Settings registry store
 */
export const useSettingsRegistry = create<SettingsRegistryState>()(
  subscribeWithSelector((set, get) => ({
    sections: [],
    valuesCache: new Map(),
    isLoading: false,
    isSaving: false,
    error: null,
    lastUpdated: null,

    load: async () => {
      const { isLoading, lastUpdated } = get();
      if (isLoading) return;
      if (lastUpdated && Date.now() - lastUpdated.getTime() < 5000) return;

      set({ isLoading: true, error: null });
      
      try {
        const data = await getSettings();
        set({
          sections: data.sections ?? [],
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (error) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load settings',
        });
      }
    },

    refresh: async () => {
      set({ isLoading: true, error: null, valuesCache: new Map() });
      
      try {
        const data = await getSettings();
        set({
          sections: data.sections ?? [],
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (error) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to refresh settings',
        });
      }
    },

    getSection: (sectionId: string) => {
      return get().sections.find(s => s.id === sectionId);
    },

    getPage: (sectionId: string, pageId: string) => {
      const section = get().sections.find(s => s.id === sectionId);
      return section?.pages.find(p => p.id === pageId);
    },

    getValues: async (pluginId: string, pageId: string) => {
      const cacheKey = `${pluginId}:${pageId}`;
      const { valuesCache } = get();
      
      // Check cache first
      if (valuesCache.has(cacheKey)) {
        return valuesCache.get(cacheKey)!;
      }
      
      // Fetch from API
      const values = await getSettingsValues(pluginId, pageId);
      
      // Update cache
      set({
        valuesCache: new Map(valuesCache).set(cacheKey, values),
      });
      
      return values;
    },

    saveValues: async (pluginId: string, pageId: string, values: Record<string, any>) => {
      set({ isSaving: true, error: null });
      
      try {
        const result = await saveSettingsValues(pluginId, pageId, values);
        
        if (result.valid) {
          // Update cache
          const cacheKey = `${pluginId}:${pageId}`;
          const { valuesCache } = get();
          set({
            valuesCache: new Map(valuesCache).set(cacheKey, values),
            isSaving: false,
          });
        } else {
          set({ isSaving: false });
        }
        
        return result;
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to save settings',
        });
        return {
          valid: false,
          errors: [{ path: '', message: error instanceof Error ? error.message : 'Unknown error' }],
        };
      }
    },

    resetToDefaults: async (pluginId: string, pageId: string) => {
      set({ isSaving: true, error: null });
      
      try {
        await resetSettings(pluginId, pageId);
        
        // Clear from cache
        const cacheKey = `${pluginId}:${pageId}`;
        const { valuesCache } = get();
        const newCache = new Map(valuesCache);
        newCache.delete(cacheKey);
        
        set({
          valuesCache: newCache,
          isSaving: false,
        });
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to reset settings',
        });
      }
    },

    getVisibleSections: (userPermissions: string[]) => {
      const sections = get().sections;
      
      return (sections ?? [])
        .filter(section => hasPermissions(section.requiredPermissions, userPermissions))
        .map(section => ({
          ...section,
          pages: (section.pages ?? []).filter(page => 
            hasPermissions(page.requiredPermissions, userPermissions)
          ),
        }))
        .filter(section => (section.pages ?? []).length > 0);
    },

    reset: () => {
      set({
        sections: [],
        valuesCache: new Map(),
        isLoading: false,
        isSaving: false,
        error: null,
        lastUpdated: null,
      });
    },
  }))
);

/**
 * Hook to use settings sections with permission filtering
 */
export function useSettingsSections(userPermissions: string[] = []) {
  const { sections, isLoading, error, load, getVisibleSections } = useSettingsRegistry();
  
  // Load on mount
  if (!isLoading && (!sections || sections.length === 0)) {
    load();
  }
  
  return {
    sections: getVisibleSections(userPermissions),
    isLoading,
    error,
    refresh: load,
  };
}

/**
 * Hook to use settings values for a specific page
 */
export function useSettingsValues(pluginId: string, pageId: string) {
  const { valuesCache, isSaving, error, getValues, saveValues, resetToDefaults } = useSettingsRegistry();
  const cacheKey = `${pluginId}:${pageId}`;
  
  return {
    values: valuesCache.get(cacheKey) ?? {},
    isSaving,
    error,
    load: () => getValues(pluginId, pageId),
    save: (values: Record<string, any>) => saveValues(pluginId, pageId, values),
    reset: () => resetToDefaults(pluginId, pageId),
  };
}

/**
 * Hook to get a specific settings section
 */
export function useSettingsSection(sectionId: string) {
  const { sections, isLoading, error, load, getSection } = useSettingsRegistry();
  
  // Load on mount
  if (!isLoading && sections.length === 0) {
    load();
  }
  
  return {
    section: getSection(sectionId),
    isLoading,
    error,
  };
}
